<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import AgentsAPI from 'dashboard/api/agents';
import ContactsAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ProposalDateField from './ProposalDateField.vue';
import ProductProviderField from './ProductProviderField.vue';
import BankLogo from 'dashboard/routes/dashboard/settings/integrations/BankLogo.vue';
import {
  proposalProvider,
  proposalProductLabel,
  proposalHistory,
} from './proposalPresentation';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import TalkviaCreditProductsAPI from 'dashboard/api/talkviaCreditProducts';
import TalkviaProposalsAPI from 'dashboard/api/talkviaProposals';

import {
  proposalStateKey,
  readProposalState,
  writeProposalState,
} from './proposalViewState';

const router = useRouter();
const store = useStore();
const { accountId } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const copy = {
  title: 'Esteira de propostas',
  subtitle:
    'Organize clientes, vendedores, assinatura, pagamento e andamento das propostas em um fluxo simples.',
  tabs: {
    proposals: 'Propostas',
    simulationDrafts: 'Simulações 48h',
    products: 'Produtos',
  },
  actions: {
    saveProposal: 'Salvar proposta',
    saveProduct: 'Salvar produto',
    newProposal: 'Nova proposta',
    newProduct: 'Novo produto',
    acceptSimulation: 'Cadastrar proposta',
    viewSimulations: 'Visualizar oferta',
    applyFilters: 'Filtrar',
    clearFilters: 'Resetar filtros',
    edit: 'Editar',
    cancel: 'Cancelar',
    pdf: 'Gerar PDF',
    openSignature: 'Assinatura',
    copyTrackingLink: 'Copiar acompanhamento',
    details: 'Detalhes',
    changeStatus: 'Alterar',
  },
  pagination: {
    showing: 'Exibindo',
    to: '–',
    of: 'de',
    perPage: 'por página:',
    ellipsis: '…',
  },
  fields: {
    title: 'Nome da proposta',
    phase: 'Fase interna',
    status: 'Andamento',
    amount: 'Valor',
    product: 'Produto',
    inbox: 'Caixa de entrada',
    client: 'Cliente',
    clientSearch: 'Buscar cliente',
    clientCpf: 'CPF do cliente',
    seller: 'Vendedor',
    term: 'Prazo',
    createdAt: 'Criada em',
    clientPaymentDate: 'Data de pagamento ao cliente',
    expectedCloseDate: 'Previsão de fechamento',
    nextFollowUpAt: 'Próximo follow-up',
    signatureUrl: 'Link para assinatura',
    productName: 'Nome do produto',
    provider: 'Banco / provedor',
    minAmount: 'Valor mínimo',
    maxAmount: 'Valor máximo',
    active: 'Ativo',
    timeline: 'Linha do tempo',
    period: 'Período',
    cpf: 'CPF',
    contactFilter: 'Contato',
    statusGroup: 'Status',
    simulationCode: 'Código',
    actions: 'Ações',
  },
  sections: {
    client: 'Dados do cliente',
    proposal: 'Dados da proposta',
    schedule: 'Prazos e assinatura',
    filters: 'Filtros da esteira',
    proposals: 'Propostas encontradas',
    products: 'Gestão de produtos',
    simulations: 'Ofertas disponíveis',
  },
  placeholders: {
    proposalTitle: 'Ex: FGTS - Maria Souza',
    clientSearch: 'Digite nome, telefone, e-mail ou CPF',
    cpf: '000.000.000-00',
    term: 'Ex: 12 parcelas',
    signatureUrl: 'https://...',
    productName: 'Ex: FGTS, INSS, CLT',
    amount: 'R$ 0,00',
    contactFilter: 'Nome ou telefone',
  },
  empty: {
    proposals: 'Nenhuma proposta cadastrada ainda.',
    products: 'Nenhum produto cadastrado ainda.',
    contacts: 'Nenhum cliente encontrado.',
    simulationDrafts:
      'Nenhuma simulação aprovada nas últimas 48h. Quando a IA consultar os bancos com sucesso, elas aparecem aqui.',
  },
  cards: {
    paid: 'Pagas',
    canceled: 'Canceladas',
    progress: 'Em andamento',
    drafts: 'Simulações 48h',
  },
  phases: {
    novo: 'Nova',
    simulacao: 'Simulação',
    documentacao: 'Documentação',
    assinatura: 'Assinatura',
    pagamento: 'Pagamento',
    finalizada: 'Finalizada',
  },
  statuses: {
    em_andamento: 'Em andamento',
    aguardando_cliente: 'Aguardando cliente',
    aguardando_banco: 'Aguardando banco',
    aguardando_assinatura: 'Aguardando assinatura',
    aprovado: 'Aprovado',
    pago: 'Pago',
    encerrado: 'Encerrado',
    recusado: 'Recusado',
    cancelado: 'Cancelado',
  },
  statusGroups: {
    all: 'Todos',
  },
  labels: {
    cpf: 'CPF',
    simulationsCount: 'simulação(ões)',
    bestOffer: 'Melhor oferta',
  },
  defaults: {
    phase: 'novo',
    currency: 'BRL',
  },
  messages: {
    noTimeline: 'Nenhum evento registrado nesta proposta.',
    noSeller: 'Não atribuído',
    savedProposal: 'Proposta salva.',
    savedProduct: 'Produto salvo.',
    loadFailed: 'Não foi possível carregar a esteira de propostas.',
    saveFailed: 'Não foi possível salvar.',
    contactSearchFailed: 'Não foi possível buscar clientes.',
    updated: 'Proposta atualizada.',
    acceptedSimulation: 'Proposta cadastrada a partir da oferta.',
    acceptFailed: 'Não foi possível enviar a simulação para propostas.',
    pdfFailed:
      'Não foi possível abrir o PDF. Verifique se o navegador bloqueou o pop-up.',
    loadingProposals: 'Consultando propostas…',
    loadingHint: 'Aplicando período, cliente e status selecionados.',
    trackingLinkCopied: 'Link público de acompanhamento copiado.',
    trackingLinkFailed: 'Não foi possível copiar o link de acompanhamento.',
  },
  eyebrow: 'Operação de crédito',
};

const activeTab = ref('proposals');
const proposals = ref([]);
const appliedFilters = ref({});
const simulationDrafts = ref([]);
const products = ref([]);
const agents = ref([]);
const contactResults = ref([]);
const contactSearch = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const isSearchingContacts = ref(false);
const editingProposalId = ref(null);
const editingProductId = ref(null);
const proposalDialogRef = ref(null);
const productDialogRef = ref(null);
const draftDialogRef = ref(null);
const selectedDraft = ref(null);
const defaultProposalEndDate = new Date();
const defaultProposalStartDate = new Date();
defaultProposalStartDate.setDate(defaultProposalStartDate.getDate() - 29);
const proposalDateRange = ref([
  defaultProposalStartDate,
  defaultProposalEndDate,
]);
const proposalRangeType = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const openProposalActionsId = ref(null);

const page = ref(1);
const perPage = ref(25);
const totalCount = ref(0);
const totalPages = ref(0);
const metaCounts = ref({
  paid_count: 0,
  paid_amount: 0,
  canceled_count: 0,
  canceled_amount: 0,
  in_progress_count: 0,
  in_progress_amount: 0,
});

const perPageOptions = [25, 50, 100];
const perPageSelectOptions = perPageOptions.map(value => ({
  label: String(value),
  value,
}));

const toDateInputValue = date => {
  const value = new Date(date);
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 10);
};

const filters = reactive({
  date_start: toDateInputValue(defaultProposalStartDate),
  date_end: toDateInputValue(defaultProposalEndDate),
  cpf: '',
  contact: '',
  status: '',
});

const proposalForm = reactive({
  title: '',
  stage: copy.defaults.phase,
  status: 'em_andamento',
  amount: 0,
  currency: copy.defaults.currency,
  talkvia_credit_product_id: '',
  inbox_id: '',
  contact_id: '',
  client_cpf: '',
  seller_id: '',
  term: '',
  client_payment_date: '',
  expected_close_date: '',
  next_follow_up_at: '',
  signature_url: '',
});

const productForm = reactive({
  name: '',
  provider: '',
  active: true,
  min_amount: 0,
  max_amount: 0,
});

const statusOptions = computed(() =>
  Object.entries(copy.statuses).map(([value, label]) => ({ value, label }))
);

const phaseOptions = computed(() =>
  Object.entries(copy.phases).map(([value, label]) => ({ value, label }))
);

const inboxOptions = computed(() => inboxes.value || []);

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});
const formatMoney = value => moneyFormatter.format(Number(value || 0));

const selectedContact = computed(() =>
  contactResults.value.find(
    contact => Number(contact.id) === Number(proposalForm.contact_id)
  )
);

const progressCount = computed(() => metaCounts.value.in_progress_count);
const paidCount = computed(() => metaCounts.value.paid_count);
const canceledCount = computed(() => metaCounts.value.canceled_count);
const simulationDraftsAmount = computed(() =>
  simulationDrafts.value.reduce(
    (total, draft) => total + Number(draft.best_released_amount || 0),
    0
  )
);

const cards = computed(() => [
  {
    label: copy.cards.paid,
    value: String(paidCount.value),
    amount: formatMoney(metaCounts.value.paid_amount),
    icon: 'i-lucide-circle-dollar-sign',
    cardClass: 'border-n-teal-6 bg-n-teal-2/40',
    iconClass: 'border-n-teal-6 bg-n-teal-3 text-n-teal-10',
    amountClass: 'text-n-teal-11',
  },
  {
    label: copy.cards.canceled,
    value: String(canceledCount.value),
    amount: formatMoney(metaCounts.value.canceled_amount),
    icon: 'i-lucide-ban',
    cardClass: 'border-n-ruby-6 bg-n-ruby-2/40',
    iconClass: 'border-n-ruby-6 bg-n-ruby-3 text-n-ruby-10',
    amountClass: 'text-n-ruby-11',
  },
  {
    label: copy.cards.progress,
    value: String(progressCount.value),
    amount: formatMoney(metaCounts.value.in_progress_amount),
    icon: 'i-lucide-list-checks',
    cardClass: 'border-n-blue-6 bg-n-blue-2/40',
    iconClass: 'border-n-blue-6 bg-n-blue-3 text-n-blue-10',
    amountClass: 'text-n-blue-11',
  },
  {
    label: copy.cards.drafts,
    value: String(simulationDrafts.value.length),
    amount: formatMoney(simulationDraftsAmount.value),
    icon: 'i-lucide-file-clock',
    cardClass: 'border-n-violet-6 bg-n-violet-2/40',
    iconClass: 'border-n-violet-6 bg-n-violet-3 text-n-violet-10',
    amountClass: 'text-n-violet-11',
  },
]);

const normalizeOptionalId = value => (value ? Number(value) : null);
const productRules = product => product.rules || {};
const parseCurrency = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return Number(digits || 0) / 100;
};
const formatCurrencyInput = value => moneyFormatter.format(Number(value || 0));
function displayName(user) {
  return user?.name || user?.available_name || user?.email || '';
}

const statusFilterOptions = computed(() => [
  { value: '', label: copy.statusGroups.all },
  ...statusOptions.value,
]);

const agentSelectOptions = computed(() => [
  { value: '', label: '-' },
  ...agents.value.map(agent => ({
    value: agent.id,
    label: displayName(agent),
  })),
]);

const productSelectOptions = computed(() => [
  { value: '', label: '-' },
  ...products.value.map(product => ({
    value: product.id,
    label: product.name,
  })),
]);

const inboxSelectOptions = computed(() => [
  { value: '', label: '-' },
  ...inboxOptions.value.map(inbox => ({ value: inbox.id, label: inbox.name })),
]);

const formatDate = value =>
  value ? new Date(value).toLocaleDateString('pt-BR') : '-';
const formatTime = value =>
  value
    ? new Date(value).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';
const formatDateTime = value =>
  value ? new Date(value).toLocaleString('pt-BR') : '-';

const updateProposalDateRange = value => {
  if (!Array.isArray(value)) return;

  const [startDate, endDate, selectedRange] = value;
  proposalDateRange.value = [startDate, endDate];
  proposalRangeType.value = selectedRange || DATE_RANGE_TYPES.CUSTOM_RANGE;
  filters.date_start = startDate ? toDateInputValue(startDate) : '';
  filters.date_end = endDate ? toDateInputValue(endDate) : '';
};

const phaseLabel = value => copy.phases[value] || value || '-';
const statusLabel = value => copy.statuses[value] || value || '-';
const labelValue = (label, value) => `${label}: ${value || '-'}`;
const statusDotClass = value =>
  ({
    em_andamento: 'bg-n-blue-9',
    aguardando_cliente: 'bg-n-amber-9',
    aguardando_banco: 'bg-n-purple-9',
    aguardando_assinatura: 'bg-n-teal-9',
    aprovado: 'bg-n-teal-9',
    pago: 'bg-n-teal-9',
    encerrado: 'bg-n-teal-9',
    recusado: 'bg-n-ruby-9',
    cancelado: 'bg-n-slate-9',
  })[value] || 'bg-n-slate-9';
const timelineText = item =>
  `${formatDateTime(item.created_at)} - ${item.message}`;
const productRangeText = product =>
  `${copy.fields.minAmount}: ${formatMoney(
    productRules(product).min_amount
  )} - ${copy.fields.maxAmount}: ${formatMoney(productRules(product).max_amount)}`;
const draftContactLabel = draft =>
  draft.contact?.name ||
  draft.contact?.phone_number ||
  draft.cpf_masked ||
  'Cliente sem identificação';
const draftExpiryText = draft =>
  `Válido até ${formatDateTime(draft.expires_at)}`;
const simulationTitle = simulation =>
  `${simulation.provider_label || simulation.provider} - ${formatDateTime(
    simulation.simulated_at
  )}`;
const draftSummaryText = draft =>
  `${copy.labels.cpf}: ${draft.cpf || draft.cpf_masked || '-'} · ${
    draft.simulations_count
  } ${copy.labels.simulationsCount}`;

const requestFilters = () => ({
  ...Object.fromEntries(
    Object.entries(filters).filter(([, value]) => String(value || '').trim())
  ),
  page: page.value,
  per_page: perPage.value,
});

const proposalAmountDisplay = computed({
  get: () => formatCurrencyInput(proposalForm.amount),
  set: value => {
    proposalForm.amount = parseCurrency(value);
  },
});

const resetProposalForm = () => {
  editingProposalId.value = null;
  contactSearch.value = '';
  Object.assign(proposalForm, {
    title: '',
    stage: copy.defaults.phase,
    status: 'em_andamento',
    amount: 0,
    currency: copy.defaults.currency,
    talkvia_credit_product_id: '',
    inbox_id: '',
    contact_id: '',
    client_cpf: '',
    seller_id: '',
    term: '',
    client_payment_date: '',
    expected_close_date: '',
    next_follow_up_at: '',
    signature_url: '',
  });
};

const openProposalDialog = () => {
  resetProposalForm();
  proposalDialogRef.value?.open();
};

const resetProductForm = () => {
  editingProductId.value = null;
  Object.assign(productForm, {
    name: '',
    provider: '',
    active: true,
    min_amount: 0,
    max_amount: 0,
  });
};

const openProductDialog = () => {
  resetProductForm();
  productDialogRef.value?.open();
};

const loadData = async () => {
  isLoading.value = true;
  try {
    const params = requestFilters();
    appliedFilters.value = { ...params };
    const [
      proposalResponse,
      simulationDraftResponse,
      productResponse,
      agentsResponse,
    ] = await Promise.allSettled([
      TalkviaProposalsAPI.get(params),
      TalkviaProposalsAPI.simulationDrafts(params),
      TalkviaCreditProductsAPI.get(),
      AgentsAPI.get(),
      store.dispatch('inboxes/get'),
    ]);

    const hasRequiredFailure = proposalResponse.status === 'rejected';

    if (proposalResponse.status === 'fulfilled') {
      const data = proposalResponse.value.data;
      if (data && data.proposals) {
        proposals.value = data.proposals;
        totalCount.value = data.meta?.total_count ?? data.proposals.length;
        totalPages.value = data.meta?.total_pages ?? 1;
        metaCounts.value = {
          paid_count: data.meta?.paid_count ?? 0,
          paid_amount: data.meta?.paid_amount ?? 0,
          canceled_count: data.meta?.canceled_count ?? 0,
          canceled_amount: data.meta?.canceled_amount ?? 0,
          in_progress_count: data.meta?.in_progress_count ?? 0,
          in_progress_amount: data.meta?.in_progress_amount ?? 0,
        };
      } else {
        proposals.value = Array.isArray(data) ? data : [];
        totalCount.value = proposals.value.length;
        totalPages.value = 1;
        metaCounts.value = {
          paid_count: 0,
          paid_amount: 0,
          canceled_count: 0,
          canceled_amount: 0,
          in_progress_count: 0,
          in_progress_amount: 0,
        };
      }
    } else {
      proposals.value = [];
      totalCount.value = 0;
      totalPages.value = 0;
      metaCounts.value = {
        paid_count: 0,
        paid_amount: 0,
        canceled_count: 0,
        canceled_amount: 0,
        in_progress_count: 0,
        in_progress_amount: 0,
      };
    }
    simulationDrafts.value =
      simulationDraftResponse.status === 'fulfilled'
        ? simulationDraftResponse.value.data || []
        : [];
    products.value =
      productResponse.status === 'fulfilled'
        ? productResponse.value.data || []
        : [];
    agents.value =
      agentsResponse.status === 'fulfilled'
        ? agentsResponse.value.data || []
        : [];

    if (hasRequiredFailure) {
      useAlert(copy.messages.loadFailed);
    }
  } catch {
    useAlert(copy.messages.loadFailed);
  } finally {
    isLoading.value = false;
  }
};

const clearFilters = async () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 29);
  Object.assign(filters, {
    date_start: toDateInputValue(startDate),
    date_end: toDateInputValue(endDate),
    cpf: '',
    contact: '',
    status: '',
  });
  proposalDateRange.value = [startDate, endDate];
  proposalRangeType.value = DATE_RANGE_TYPES.LAST_30_DAYS;
  page.value = 1;
  await loadData();
};

const goToPage = async n => {
  if (n < 1 || n > totalPages.value) return;
  page.value = n;
  await loadData();
};

const changePerPage = async val => {
  perPage.value = val;
  page.value = 1;
  await loadData();
};

const openDetail = proposal => {
  router.push(`/app/accounts/${accountId.value}/propostas/${proposal.id}`);
};

const onRowClick = (event, proposal) => {
  if (
    event.target.closest(
      'button, select, a, input, label, details, summary, [role=combobox]'
    )
  )
    return;
  openDetail(proposal);
};

const searchContacts = async () => {
  if (!contactSearch.value || contactSearch.value.length < 2) {
    contactResults.value = selectedContact.value ? [selectedContact.value] : [];
    return;
  }

  isSearchingContacts.value = true;
  try {
    const { data } = await ContactsAPI.search(contactSearch.value, 1);
    contactResults.value = data.payload || [];
  } catch {
    useAlert(copy.messages.contactSearchFailed);
  } finally {
    isSearchingContacts.value = false;
  }
};

const selectContact = contact => {
  proposalForm.contact_id = contact.id;
  contactSearch.value = `${contact.name || contact.phone_number || contact.email}`;
  const cpf =
    contact.custom_attributes?.cpf ||
    contact.custom_attributes?.CPF ||
    contact.additional_attributes?.cpf;
  if (cpf && !proposalForm.client_cpf) proposalForm.client_cpf = cpf;
};

const proposalPayload = () => ({
  title: proposalForm.title,
  stage: proposalForm.stage || copy.defaults.phase,
  status: proposalForm.status,
  amount: proposalForm.amount || 0,
  currency: proposalForm.currency || copy.defaults.currency,
  talkvia_credit_product_id: normalizeOptionalId(
    proposalForm.talkvia_credit_product_id
  ),
  inbox_id: normalizeOptionalId(proposalForm.inbox_id),
  contact_id: normalizeOptionalId(proposalForm.contact_id),
  seller_id: normalizeOptionalId(proposalForm.seller_id),
  client_cpf: proposalForm.client_cpf,
  term: proposalForm.term,
  client_payment_date: proposalForm.client_payment_date || null,
  expected_close_date: proposalForm.expected_close_date || null,
  next_follow_up_at: proposalForm.next_follow_up_at || null,
  signature_url: proposalForm.signature_url,
});

const saveProposal = async () => {
  if (
    isSaving.value ||
    !proposalForm.title ||
    !proposalForm.status ||
    !proposalForm.stage
  )
    return;
  isSaving.value = true;
  try {
    if (editingProposalId.value) {
      await TalkviaProposalsAPI.update(
        editingProposalId.value,
        proposalPayload()
      );
    } else {
      await TalkviaProposalsAPI.create(proposalPayload());
    }
    useAlert(copy.messages.savedProposal);
    resetProposalForm();
    proposalDialogRef.value?.close();
    await loadData();
  } catch {
    useAlert(copy.messages.saveFailed);
  } finally {
    isSaving.value = false;
  }
};

const productPayload = () => ({
  name: productForm.name,
  provider: productForm.provider,
  active: productForm.active,
  rules: {
    min_amount: Number(productForm.min_amount || 0),
    max_amount: Number(productForm.max_amount || 0),
  },
});

const saveProduct = async () => {
  isSaving.value = true;
  try {
    let savedProduct = null;
    if (editingProductId.value) {
      const response = await TalkviaCreditProductsAPI.update(
        editingProductId.value,
        productPayload()
      );
      savedProduct = response.data;
    } else {
      const response = await TalkviaCreditProductsAPI.create(productPayload());
      savedProduct = response.data;
    }
    useAlert(copy.messages.savedProduct);
    resetProductForm();
    await loadData();
    if (savedProduct?.id) {
      proposalForm.talkvia_credit_product_id = savedProduct.id;
    }
    productDialogRef.value?.close();
  } catch {
    useAlert(copy.messages.saveFailed);
  } finally {
    isSaving.value = false;
  }
};

const editProposal = proposal => {
  editingProposalId.value = proposal.id;
  activeTab.value = 'proposals';
  contactResults.value = proposal.contact ? [proposal.contact] : [];
  contactSearch.value = proposal.contact?.name || '';
  Object.assign(proposalForm, {
    title: proposal.title,
    stage: proposal.stage,
    status: proposal.status,
    amount: proposal.amount,
    currency: proposal.currency,
    talkvia_credit_product_id: proposal.talkvia_credit_product_id || '',
    inbox_id: proposal.inbox_id || '',
    contact_id: proposal.contact_id || '',
    client_cpf: proposal.client_cpf || '',
    seller_id: proposal.seller_id || '',
    term: proposal.term || '',
    client_payment_date: proposal.client_payment_date || '',
    expected_close_date: proposal.expected_close_date || '',
    next_follow_up_at: proposal.next_follow_up_at || '',
    signature_url: proposal.signature_url || '',
  });
  proposalDialogRef.value?.open();
};

const editProduct = product => {
  editingProductId.value = product.id;
  activeTab.value = 'products';
  Object.assign(productForm, {
    name: product.name,
    provider: product.provider || '',
    active: product.active,
    min_amount: productRules(product).min_amount || 0,
    max_amount: productRules(product).max_amount || 0,
  });
  productDialogRef.value?.open();
};

const selectedIds = ref([]);
const allMatching = ref(false);
const bulkDialogRef = ref(null);
const bulkStatus = ref('');
const bulkSeller = ref('');
const bulkChangeSeller = ref(false);
const selectionCount = computed(() =>
  allMatching.value ? totalCount.value : selectedIds.value.length
);
const allOnPage = computed(
  () =>
    proposals.value.length > 0 &&
    proposals.value.every(item => selectedIds.value.includes(item.id))
);
function clearSelection() {
  selectedIds.value = [];
  allMatching.value = false;
}
const selectPage = checked => {
  allMatching.value = false;
  const pageIds = proposals.value.map(item => item.id);
  selectedIds.value = checked
    ? [...new Set([...selectedIds.value, ...pageIds])]
    : selectedIds.value.filter(id => !pageIds.includes(id));
};
const selectProposal = (id, checked) => {
  if (allMatching.value) {
    allMatching.value = false;
    selectedIds.value = proposals.value.map(item => item.id);
  }
  selectedIds.value = checked
    ? [...new Set([...selectedIds.value, id])]
    : selectedIds.value.filter(value => value !== id);
};
watch(filters, clearSelection, { deep: true });
const applyBulk = async () => {
  if (!selectionCount.value || (!bulkStatus.value && !bulkChangeSeller.value))
    return;
  isSaving.value = true;
  try {
    const changes = {};
    if (bulkStatus.value) changes.status = bulkStatus.value;
    if (bulkChangeSeller.value) changes.seller_id = bulkSeller.value || null;
    const { data } = await TalkviaProposalsAPI.bulkUpdate({
      ...appliedFilters.value,
      ids: selectedIds.value,
      all_matching: allMatching.value,
      expected_count: selectionCount.value,
      changes,
    });
    useAlert(`${data.updated_count} propostas atualizadas.`);
    clearSelection();
    bulkDialogRef.value?.close();
    await loadData();
  } catch {
    useAlert(
      'Não foi possível atualizar a seleção. Atualize a lista e tente novamente.'
    );
  } finally {
    isSaving.value = false;
  }
};

const openDraftDialog = draft => {
  selectedDraft.value = draft;
  draftDialogRef.value?.open();
};

const acceptSimulation = async simulation => {
  if (!simulation?.id) return;
  isSaving.value = true;
  try {
    await TalkviaProposalsAPI.acceptSimulationDraft({
      result_id: simulation.id,
    });
    useAlert(copy.messages.acceptedSimulation);
    draftDialogRef.value?.close();
    selectedDraft.value = null;
    activeTab.value = 'proposals';
    await loadData();
  } catch {
    useAlert(copy.messages.acceptFailed);
  } finally {
    isSaving.value = false;
  }
};

const escapeHtml = value =>
  String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const proposalTimeline = proposal => proposalHistory(proposal);

const printProposal = proposal => {
  const timeline = proposalTimeline(proposal)
    .map(
      item =>
        `<li><strong>${escapeHtml(formatDateTime(item.created_at))}</strong> - ${escapeHtml(item.message)}</li>`
    )
    .join('');
  const content = `
    <html>
      <head>
        <title>${escapeHtml(proposal.title)}</title>
        <style>
          body { font-family: Arial, sans-serif; color: #0f172a; margin: 32px; }
          h1 { margin-bottom: 4px; }
          .muted { color: #64748b; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 20px; }
          .box { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; }
          .label { color: #64748b; font-size: 12px; text-transform: uppercase; }
          .value { margin-top: 4px; font-size: 16px; font-weight: 600; }
          ul { line-height: 1.7; }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(proposal.title)}</h1>
        <p class="muted">Espelho da proposta gerado pela Talkvia</p>
        <div class="grid">
          <div class="box"><div class="label">Cliente</div><div class="value">${escapeHtml(proposal.contact?.name || '-')}</div></div>
          <div class="box"><div class="label">CPF</div><div class="value">${escapeHtml(proposal.client_cpf || '-')}</div></div>
          <div class="box"><div class="label">Produto</div><div class="value">${escapeHtml(proposal.product?.name || '-')}</div></div>
          <div class="box"><div class="label">Valor</div><div class="value">${escapeHtml(formatMoney(proposal.amount))}</div></div>
          <div class="box"><div class="label">Status</div><div class="value">${escapeHtml(statusLabel(proposal.status))}</div></div>
          <div class="box"><div class="label">Vendedor</div><div class="value">${escapeHtml(displayName(proposal.seller) || '-')}</div></div>
          <div class="box"><div class="label">Prazo</div><div class="value">${escapeHtml(proposal.term || '-')}</div></div>
          <div class="box"><div class="label">Pagamento ao cliente</div><div class="value">${escapeHtml(formatDate(proposal.client_payment_date))}</div></div>
        </div>
        <h2>Linha do tempo</h2>
        <ul>${timeline || '<li>Sem eventos registrados.</li>'}</ul>
      </body>
    </html>
  `;
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    useAlert(copy.messages.pdfFailed);
    return;
  }
  printWindow.document.open();
  printWindow.document.write(content);
  printWindow.document.close();
  printWindow.focus();
  window.setTimeout(() => printWindow.print(), 250);
};

const openSignature = proposal => {
  if (!proposal.signature_url) return;
  window.open(proposal.signature_url, '_blank', 'noopener,noreferrer');
};

const copyTrackingLink = async proposal => {
  if (!proposal.public_url) return;

  try {
    const url = new URL(proposal.public_url, window.location.origin).toString();
    await navigator.clipboard.writeText(url);
    useAlert(copy.messages.trackingLinkCopied);
  } catch {
    useAlert(copy.messages.trackingLinkFailed);
  }
};

const toggleProposalActionsMenu = proposalId => {
  openProposalActionsId.value =
    openProposalActionsId.value === proposalId ? null : proposalId;
};

const closeProposalActionsMenu = () => {
  openProposalActionsId.value = null;
};

const proposalActionMenuItems = proposal =>
  [
    {
      label: copy.actions.details,
      action: 'details',
      value: 'details',
      icon: 'i-lucide-eye',
    },
    {
      label: 'Editar proposta, andamento e vendedor',
      action: 'edit',
      value: 'edit',
      icon: 'i-lucide-pencil',
    },
    {
      label: copy.actions.pdf,
      action: 'pdf',
      value: 'pdf',
      icon: 'i-lucide-file-text',
    },
    proposal.signature_url
      ? {
          label: copy.actions.openSignature,
          action: 'signature',
          value: 'signature',
          icon: 'i-lucide-signature',
        }
      : null,
    proposal.public_url
      ? {
          label: copy.actions.copyTrackingLink,
          action: 'tracking',
          value: 'tracking',
          icon: 'i-lucide-link',
        }
      : null,
  ].filter(Boolean);

const handleProposalAction = ({ action }, proposal) => {
  closeProposalActionsMenu();

  if (action === 'details') {
    openDetail(proposal);
  } else if (action === 'edit') {
    editProposal(proposal);
  } else if (action === 'pdf') {
    printProposal(proposal);
  } else if (action === 'signature') {
    openSignature(proposal);
  } else if (action === 'tracking') {
    copyTrackingLink(proposal);
  }
};

const stateKey = () =>
  proposalStateKey(accountId.value, store.getters.getCurrentUser?.id);
const persistFilters = () => {
  try {
    writeProposalState(sessionStorage, stateKey(), {
      filters,
      page: page.value,
      perPage: perPage.value,
      activeTab: activeTab.value,
      rangeType: proposalRangeType.value,
    });
  } catch {
    /* Storage may be unavailable in private browsing. */
  }
};
watch([filters, page, perPage, activeTab, proposalRangeType], persistFilters, {
  deep: true,
});
onMounted(() => {
  try {
    const saved = readProposalState(sessionStorage, stateKey());
    if (saved?.filters) {
      Object.keys(filters).forEach(key => {
        if (typeof saved.filters[key] === 'string')
          filters[key] = saved.filters[key];
      });
      proposalDateRange.value = [filters.date_start, filters.date_end].map(
        value => (value ? new Date(`${value}T00:00:00`) : null)
      );
      proposalRangeType.value =
        saved.rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
      page.value = Math.max(1, Number(saved.page) || 1);
      perPage.value = perPageOptions.includes(saved.perPage)
        ? saved.perPage
        : 25;
      activeTab.value =
        saved.activeTab === 'simulationDrafts' ? saved.activeTab : 'proposals';
    }
  } catch {
    /* Use the last 30 days when saved filters cannot be read. */
  }
  loadData();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="h-full w-full overflow-y-auto bg-n-surface-1 px-3 py-4 sm:px-5 lg:px-6 lg:py-5"
  >
    <div class="mx-auto flex w-full max-w-[1600px] flex-col gap-4">
      <section
        class="overflow-hidden rounded-3xl border border-n-brand/15 bg-gradient-to-br from-n-brand/10 via-n-solid-2 to-n-solid-2 shadow-sm"
      >
        <header
          class="flex flex-col gap-4 px-5 pb-5 pt-6 sm:px-7 lg:flex-row lg:items-start lg:justify-between"
        >
          <div class="min-w-0">
            <p
              class="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-n-brand"
            >
              {{ copy.eyebrow }}
            </p>
            <h1 class="mb-1 text-2xl font-bold text-n-slate-12 sm:text-3xl">
              {{ copy.title }}
            </h1>
            <p class="mb-0 max-w-3xl text-sm leading-6 text-n-slate-11">
              {{ copy.subtitle }}
            </p>
          </div>
          <Button
            v-if="activeTab === 'proposals'"
            type="button"
            :label="copy.actions.newProposal"
            icon="i-lucide-plus"
            class="w-full shrink-0 sm:w-auto"
            @click="openProposalDialog"
          />
        </header>
        <div
          class="flex w-full overflow-x-auto border-y border-n-weak bg-n-solid-1 px-2 sm:px-4"
        >
          <Button
            :label="copy.tabs.proposals"
            color="slate"
            variant="ghost"
            class="shrink-0 flex-1 !rounded-none border-b-2 lg:flex-none"
            :class="
              activeTab === 'proposals'
                ? 'border-n-brand text-n-brand'
                : 'border-transparent'
            "
            @click="activeTab = 'proposals'"
          />
          <Button
            :label="copy.tabs.simulationDrafts"
            color="slate"
            variant="ghost"
            class="shrink-0 flex-1 !rounded-none border-b-2 lg:flex-none"
            :class="
              activeTab === 'simulationDrafts'
                ? 'border-n-brand text-n-brand'
                : 'border-transparent'
            "
            @click="activeTab = 'simulationDrafts'"
          />
        </div>

        <div class="grid grid-cols-2 gap-2 p-3 sm:grid-cols-4 sm:p-4">
          <div
            v-for="card in cards"
            :key="card.label"
            class="flex min-w-0 items-center gap-3 rounded-2xl border px-3 py-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            :class="card.cardClass"
          >
            <span
              class="grid size-9 shrink-0 place-content-center rounded-lg border sm:size-10"
              :class="card.iconClass"
            >
              <Icon :icon="card.icon" class="size-5" />
            </span>
            <div class="min-w-0">
              <p class="mb-0 truncate text-xs font-medium text-n-slate-10">
                {{ card.label }}
              </p>
              <div class="flex min-w-0 items-baseline gap-2">
                <strong class="text-lg text-n-slate-12">
                  {{ card.value }}
                </strong>
                <span
                  v-if="card.amount"
                  class="truncate text-xs font-semibold"
                  :class="card.amountClass"
                >
                  {{ card.amount }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <form
        v-if="activeTab !== 'products'"
        class="relative z-20 overflow-visible rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm"
        @submit.prevent="
          page = 1;
          loadData();
        "
      >
        <div
          class="flex flex-col gap-3 border-b border-n-weak px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex items-center gap-2">
            <span
              class="grid size-8 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              <Icon icon="i-lucide-list-filter" class="size-4" />
            </span>
            <h2 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ copy.sections.filters }}
            </h2>
          </div>
          <div class="flex gap-2">
            <Button
              type="button"
              :label="copy.actions.clearFilters"
              color="slate"
              variant="ghost"
              class="flex-1 sm:flex-none"
              :disabled="isLoading"
              @click="clearFilters"
            />
            <Button
              type="submit"
              :label="copy.actions.applyFilters"
              icon="i-lucide-search"
              :is-loading="isLoading"
              :disabled="isLoading"
              class="flex-1 sm:flex-none"
            />
          </div>
        </div>

        <div
          class="grid min-w-0 gap-3 p-4 md:grid-cols-2 xl:grid-cols-12 xl:items-end"
        >
          <div class="flex min-w-0 flex-col gap-1 md:col-span-2 xl:col-span-4">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ copy.fields.period }}
            </label>
            <WootDatePicker
              v-model:date-range="proposalDateRange"
              v-model:range-type="proposalRangeType"
              class="!w-full !min-w-0 !shrink [&>div:first-child]:!w-full [&>div:first-child]:!min-w-0 [&>div:first-child>button]:!min-w-0 [&>div:first-child>button]:!flex-1"
              @date-range-changed="updateProposalDateRange"
            />
          </div>
          <div class="min-w-0 xl:col-span-2">
            <Input
              v-model="filters.cpf"
              :label="copy.fields.cpf"
              :placeholder="copy.placeholders.cpf"
              class="w-full min-w-0"
            />
          </div>
          <div class="min-w-0 xl:col-span-3">
            <Input
              v-model="filters.contact"
              :label="copy.fields.contactFilter"
              :placeholder="copy.placeholders.contactFilter"
              class="w-full min-w-0"
            />
          </div>
          <div class="flex min-w-0 flex-col gap-1 xl:col-span-3">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ copy.fields.statusGroup }}
            </label>
            <ComboBox
              v-model="filters.status"
              :options="statusFilterOptions"
              class="w-full [&>select]:w-full"
              teleport-to-body
            />
          </div>
        </div>
      </form>

      <div v-if="activeTab === 'proposals'" class="flex min-w-0 flex-col gap-4">
        <div class="px-1">
          <h2 class="mb-0 text-base font-semibold text-n-slate-12">
            {{ copy.sections.proposals }}
          </h2>
          <p class="mb-0 text-xs text-n-slate-10">
            {{ totalCount }} {{ copy.tabs.proposals.toLowerCase() }}
          </p>
        </div>

        <main class="flex min-w-0 flex-col gap-4">
          <div
            v-if="proposals.length && !isLoading"
            class="flex flex-wrap items-center gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-3"
          >
            <Button
              label="Selecionar página"
              variant="ghost"
              color="slate"
              size="sm"
              @click="selectPage(true)"
            />
            <Button
              :label="`Selecionar todas (${totalCount})`"
              variant="ghost"
              color="slate"
              size="sm"
              @click="allMatching = true"
            />
            <span class="text-xs text-n-slate-11"
              >{{ selectionCount }} selecionadas</span
            >
            <Button
              v-if="selectionCount"
              label="Limpar seleção"
              variant="ghost"
              color="slate"
              size="sm"
              @click="clearSelection"
            />
            <Button
              label="Ação em massa"
              size="sm"
              :disabled="!selectionCount || isSaving"
              @click="bulkDialogRef?.open()"
            />
          </div>
          <div
            v-if="isLoading"
            class="flex min-h-52 flex-col items-center justify-center rounded-2xl border border-n-brand/20 bg-n-brand/5 p-10 text-center text-n-slate-11"
            role="status"
            aria-live="polite"
          >
            <span
              class="mb-4 grid size-12 place-items-center rounded-2xl bg-n-brand/10 text-n-brand"
            >
              <Icon icon="i-lucide-loader-circle" class="size-6 animate-spin" />
            </span>
            <strong class="text-sm font-semibold text-n-slate-12">
              {{ copy.messages.loadingProposals }}
            </strong>
            <span class="mt-1 text-xs text-n-slate-10">
              {{ copy.messages.loadingHint }}
            </span>
          </div>
          <div
            v-else-if="!proposals.length"
            class="rounded-xl border border-dashed border-n-weak bg-n-solid-2 p-10 text-center text-n-slate-11"
          >
            {{ copy.empty.proposals }}
          </div>
          <div
            v-else
            class="overflow-visible rounded-xl border border-n-weak bg-n-solid-2 shadow-sm"
          >
            <div
              class="hidden grid-cols-[minmax(0,2fr)_minmax(0,1fr)_9rem_8rem_11.5rem_minmax(0,1fr)_3rem] gap-4 border-b border-n-weak bg-n-solid-1 px-5 py-3 text-xs font-semibold uppercase text-n-slate-10 xl:grid"
            >
              <div class="flex items-center gap-3">
                <Checkbox
                  :model-value="allMatching || allOnPage"
                  :indeterminate="
                    selectionCount > 0 && !allOnPage && !allMatching
                  "
                  aria-label="Selecionar propostas desta página"
                  @update:model-value="selectPage"
                />
                <span>{{ copy.fields.title }}</span>
              </div>
              <span>{{ copy.fields.client }}</span>
              <span>{{ copy.fields.createdAt }}</span>
              <span>{{ copy.fields.amount }}</span>
              <span>{{ copy.fields.status }}</span>
              <span>{{ copy.fields.seller }}</span>
              <span class="text-right">{{ copy.fields.actions }}</span>
            </div>
            <article
              v-for="proposal in proposals"
              :key="proposal.id"
              class="grid cursor-pointer gap-4 border-b border-n-weak px-4 py-5 transition-colors last:border-b-0 hover:bg-n-alpha-1 sm:grid-cols-2 sm:px-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_9rem_8rem_11.5rem_minmax(0,1fr)_3rem] xl:items-start"
              @click="onRowClick($event, proposal)"
            >
              <div class="min-w-0 sm:col-span-2 xl:col-span-1">
                <div class="flex items-start gap-3">
                  <Checkbox
                    :model-value="
                      allMatching || selectedIds.includes(proposal.id)
                    "
                    :aria-label="`Selecionar proposta ${proposal.title}`"
                    @update:model-value="
                      checked => selectProposal(proposal.id, checked)
                    "
                  />
                  <span
                    class="flex size-9 shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 p-1.5"
                  >
                    <BankLogo :provider="proposalProvider(proposal)" />
                  </span>
                  <div class="min-w-0">
                    <h3
                      class="mb-1 break-words text-sm font-semibold text-n-slate-12"
                      :title="proposal.title"
                    >
                      {{ proposal.title }}
                    </h3>
                    <span
                      class="inline-flex rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
                    >
                      {{ proposalProductLabel(proposal) }}
                    </span>
                  </div>
                </div>
                <details class="mt-3 text-xs text-n-slate-11" @click.stop>
                  <summary
                    class="cursor-pointer rounded-md py-1 font-medium text-n-brand"
                  >
                    {{ copy.fields.timeline }}
                  </summary>
                  <ol class="mt-2 space-y-3 border-l-2 border-n-weak pl-3">
                    <li
                      v-for="(item, index) in proposalTimeline(proposal)"
                      :key="index"
                    >
                      {{ timelineText(item) }}
                    </li>
                    <li v-if="!proposalTimeline(proposal).length">
                      {{ copy.messages.noTimeline }}
                    </li>
                  </ol>
                </details>
                <button
                  v-if="proposal.public_url"
                  type="button"
                  class="mt-1 inline-flex items-center gap-1.5 rounded-md py-1 text-xs font-medium text-n-brand hover:underline"
                  @click.stop="copyTrackingLink(proposal)"
                >
                  <Icon icon="i-lucide-link" class="size-3.5" />
                  {{ copy.actions.copyTrackingLink }}
                </button>
              </div>
              <div class="min-w-0 break-words text-sm text-n-slate-12">
                <span class="mb-1 block text-xs text-n-slate-11 xl:hidden">{{
                  copy.fields.client
                }}</span>
                {{ proposal.contact?.name || proposal.client_cpf || '-' }}
              </div>
              <div class="text-sm text-n-slate-12">
                <span class="mb-1 block text-xs text-n-slate-11 xl:hidden">{{
                  copy.fields.createdAt
                }}</span>
                <time
                  :datetime="proposal.created_at || undefined"
                  class="block"
                >
                  {{ formatDate(proposal.created_at) }}
                  <span class="mt-1 block text-xs text-n-slate-11">{{
                    formatTime(proposal.created_at)
                  }}</span>
                </time>
              </div>
              <div>
                <span class="mb-1 block text-xs text-n-slate-11 xl:hidden">{{
                  copy.fields.amount
                }}</span>
                <strong class="text-sm text-n-slate-12">{{
                  formatMoney(proposal.amount)
                }}</strong>
                <span class="mt-1 block text-xs text-n-slate-11">{{
                  labelValue(copy.fields.term, proposal.term)
                }}</span>
                <span
                  v-if="proposal.client_payment_date"
                  class="mt-2 block text-xs text-n-slate-11"
                >
                  {{
                    labelValue(
                      copy.fields.clientPaymentDate,
                      formatDate(proposal.client_payment_date)
                    )
                  }}
                </span>
              </div>
              <div>
                <span class="mb-1 block text-xs text-n-slate-11 xl:hidden">{{
                  copy.fields.status
                }}</span>
                <span
                  class="inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-md border border-n-weak bg-n-alpha-1 px-2 text-[11px] font-medium leading-none text-n-slate-12"
                >
                  <span
                    class="size-1.5 shrink-0 rounded-full"
                    :class="statusDotClass(proposal.status)"
                    aria-hidden="true"
                  />
                  {{ statusLabel(proposal.status) }}
                </span>
                <span class="mt-2 block text-xs text-n-slate-11">{{
                  phaseLabel(proposal.stage)
                }}</span>
              </div>
              <div class="min-w-0 break-words text-sm text-n-slate-11">
                <span class="mb-1 block text-xs xl:hidden">{{
                  copy.fields.seller
                }}</span>
                {{ displayName(proposal.seller) || copy.messages.noSeller }}
              </div>

              <div
                v-on-clickaway="closeProposalActionsMenu"
                class="relative flex justify-start lg:justify-end"
              >
                <Button
                  v-tooltip.top="copy.fields.actions"
                  type="button"
                  icon="i-lucide-ellipsis-vertical"
                  color="slate"
                  variant="ghost"
                  size="sm"
                  :class="
                    openProposalActionsId === proposal.id ? 'bg-n-alpha-2' : ''
                  "
                  @click.stop="toggleProposalActionsMenu(proposal.id)"
                />
                <DropdownMenu
                  v-if="openProposalActionsId === proposal.id"
                  :menu-items="proposalActionMenuItems(proposal)"
                  class="right-0 top-9 w-64 text-left"
                  @click.stop
                  @action="handleProposalAction($event, proposal)"
                />
              </div>
            </article>
          </div>

          <!-- Pagination -->
          <div
            v-if="totalPages > 1 || proposals.length > 0"
            class="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3 shadow-sm"
          >
            <div class="flex items-center gap-3 text-sm text-n-slate-11">
              <span>
                {{ copy.pagination.showing }}
                {{ (page - 1) * perPage + 1 }}{{ copy.pagination.to
                }}{{ Math.min(page * perPage, totalCount) }}
                {{ copy.pagination.of }}
                {{ totalCount }}
              </span>
              <div class="flex items-center gap-1.5">
                <span class="text-xs">{{ copy.pagination.perPage }}</span>
                <Select
                  v-model="perPage"
                  :options="perPageSelectOptions"
                  class="[&>select]:h-8 [&>select]:py-1 [&>select]:text-xs"
                  @update:model-value="changePerPage"
                />
              </div>
            </div>
            <div class="flex items-center gap-1">
              <Button
                type="button"
                icon="i-lucide-chevron-left"
                color="slate"
                variant="ghost"
                size="sm"
                :disabled="page <= 1"
                @click="goToPage(page - 1)"
              />
              <template v-for="n in totalPages" :key="n">
                <Button
                  v-if="
                    n === 1 ||
                    n === totalPages ||
                    (n >= page - 1 && n <= page + 1)
                  "
                  type="button"
                  :label="n"
                  :variant="n === page ? 'solid' : 'ghost'"
                  :color="n === page ? 'blue' : 'slate'"
                  size="sm"
                  @click="goToPage(n)"
                />
                <span
                  v-else-if="n === page - 2 || n === page + 2"
                  class="select-none px-1 text-n-slate-10"
                >
                  {{ copy.pagination.ellipsis }}
                </span>
              </template>
              <Button
                type="button"
                icon="i-lucide-chevron-right"
                color="slate"
                variant="ghost"
                size="sm"
                :disabled="page >= totalPages"
                @click="goToPage(page + 1)"
              />
            </div>
          </div>
        </main>
      </div>

      <div
        v-else-if="activeTab === 'simulationDrafts'"
        class="flex min-w-0 flex-col gap-4"
      >
        <div class="px-1">
          <h2 class="mb-0 text-base font-semibold text-n-slate-12">
            {{ copy.sections.simulations }}
          </h2>
          <p class="mb-0 text-xs text-n-slate-10">
            {{ simulationDrafts.length }}
            {{ copy.tabs.simulationDrafts.toLowerCase() }}
          </p>
        </div>
        <div
          v-if="isLoading"
          class="rounded-lg border border-n-weak bg-n-solid-2 p-8 text-center text-n-slate-11"
        >
          {{ copy.tabs.simulationDrafts }}
        </div>
        <div
          v-else-if="!simulationDrafts.length"
          class="rounded-lg border border-dashed border-n-weak bg-n-solid-2 p-8 text-center text-n-slate-11"
        >
          {{ copy.empty.simulationDrafts }}
        </div>
        <div
          v-else
          class="grid items-start gap-4 md:grid-cols-2 2xl:grid-cols-3"
        >
          <article
            v-for="draft in simulationDrafts"
            :key="draft.id"
            class="flex h-full min-w-0 flex-col rounded-xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
          >
            <div class="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <h3
                  class="mb-1 truncate text-base font-semibold text-n-slate-12"
                >
                  {{ draftContactLabel(draft) }}
                </h3>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ draftSummaryText(draft) }}
                </p>
              </div>
              <div class="shrink-0 text-right">
                <span class="block text-xs text-n-slate-10">
                  {{ copy.labels.bestOffer }}
                </span>
                <strong class="text-xl font-semibold text-n-brand">
                  {{ formatMoney(draft.best_released_amount) }}
                </strong>
              </div>
            </div>
            <div
              class="mb-4 flex flex-1 flex-wrap content-start gap-2 text-xs text-n-slate-11"
            >
              <span class="rounded-md bg-n-alpha-2 px-2 py-1">
                {{ draftExpiryText(draft) }}
              </span>
              <span
                v-for="simulation in draft.simulations"
                :key="simulation.id"
                class="rounded-md bg-n-alpha-2 px-2 py-1"
              >
                {{ simulation.provider_label }}:
                {{ formatMoney(simulation.released_amount) }}
              </span>
            </div>
            <Button
              type="button"
              :label="copy.actions.viewSimulations"
              icon="i-lucide-eye"
              class="w-full"
              size="sm"
              @click="openDraftDialog(draft)"
            />
          </article>
        </div>
      </div>

      <div
        v-else-if="activeTab === 'products'"
        class="grid gap-4 xl:grid-cols-[24rem_minmax(0,1fr)]"
      >
        <aside
          class="rounded-xl border border-n-weak bg-n-solid-2 p-5 shadow-sm"
        >
          <div class="mb-4 flex items-center justify-between gap-3">
            <h2 class="mb-0 text-base font-semibold text-n-slate-12">
              {{
                editingProductId ? copy.actions.edit : copy.actions.newProduct
              }}
            </h2>
            <Button
              v-if="editingProductId"
              type="button"
              :label="copy.actions.cancel"
              color="slate"
              variant="link"
              size="sm"
              @click="resetProductForm"
            />
          </div>
          <form class="flex flex-col gap-3" @submit.prevent="saveProduct">
            <Input
              v-model="productForm.name"
              required
              :label="copy.fields.productName"
              :placeholder="copy.placeholders.productName"
            />
            <ProductProviderField
              :key="editingProductId"
              v-model="productForm.provider"
              :label="copy.fields.provider"
            />
            <div class="grid gap-3 sm:grid-cols-2">
              <Input
                v-model="productForm.min_amount"
                :label="copy.fields.minAmount"
                type="number"
                min="0"
                step="0.01"
              />
              <Input
                v-model="productForm.max_amount"
                :label="copy.fields.maxAmount"
                type="number"
                min="0"
                step="0.01"
              />
            </div>
            <label class="flex items-center gap-2 text-sm text-n-slate-11">
              <Checkbox v-model="productForm.active" />
              {{ copy.fields.active }}
            </label>
            <Button
              type="submit"
              class="mt-1"
              :label="copy.actions.saveProduct"
              :is-loading="isSaving"
              :disabled="isSaving"
            />
          </form>
        </aside>

        <main
          class="min-w-0 rounded-xl border border-n-weak bg-n-solid-2 p-5 shadow-sm"
        >
          <div class="mb-4">
            <h2 class="mb-0 text-base font-semibold text-n-slate-12">
              {{ copy.sections.products }}
            </h2>
            <p class="mb-0 text-xs text-n-slate-10">
              {{ products.length }} {{ copy.tabs.products.toLowerCase() }}
            </p>
          </div>
          <div
            v-if="!products.length"
            class="rounded-lg border border-dashed border-n-weak p-8 text-center text-n-slate-11"
          >
            {{ copy.empty.products }}
          </div>
          <div v-else class="grid gap-3 lg:grid-cols-2">
            <article
              v-for="product in products"
              :key="product.id"
              class="rounded-lg border border-n-weak bg-n-solid-1 p-4"
            >
              <div class="mb-2 flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <h3
                    class="mb-1 truncate text-base font-semibold text-n-slate-12"
                  >
                    {{ product.name }}
                  </h3>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ product.provider || '-' }}
                  </p>
                </div>
                <span
                  class="rounded-md px-2 py-1 text-xs font-medium"
                  :class="
                    product.active
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  {{
                    product.active
                      ? copy.fields.active
                      : copy.statuses.cancelado
                  }}
                </span>
              </div>
              <p class="mb-3 text-sm text-n-slate-11">
                {{ productRangeText(product) }}
              </p>
              <Button
                type="button"
                class="mt-3"
                :label="copy.actions.edit"
                color="slate"
                variant="faded"
                size="sm"
                @click="editProduct(product)"
              />
            </article>
          </div>
        </main>
      </div>
    </div>

    <Dialog
      ref="bulkDialogRef"
      title="Atualizar propostas selecionadas"
      :description="`A alteração será aplicada a ${selectionCount} propostas. Revise antes de confirmar.`"
      confirm-button-label="Confirmar atualização"
      :is-loading="isSaving"
      :disable-confirm-button="
        !selectionCount || (!bulkStatus && !bulkChangeSeller)
      "
      @confirm="applyBulk"
    >
      <div class="grid gap-4">
        <div>
          <label class="mb-2 block text-sm">Status</label
          ><ComboBox
            v-model="bulkStatus"
            :options="[
              { value: '', label: 'Manter status atual' },
              ...statusOptions,
            ]"
          />
        </div>
        <label class="flex items-center gap-2 text-sm"
          ><Checkbox v-model="bulkChangeSeller" />Alterar vendedor</label
        >
        <ComboBox
          v-if="bulkChangeSeller"
          v-model="bulkSeller"
          :options="agentSelectOptions"
        />
      </div>
    </Dialog>

    <Dialog
      ref="proposalDialogRef"
      width="4xl"
      sticky-header-footer
      position="top"
      overflow-y-auto
      :title="editingProposalId ? 'Editar proposta' : copy.actions.newProposal"
      description="Revise os dados comerciais, o responsável e os próximos passos da proposta."
      :show-confirm-button="false"
      :show-cancel-button="false"
      @confirm="saveProposal"
    >
      <div class="grid gap-4">
        <section class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
          <h4 class="mb-3 text-sm font-semibold text-n-slate-12">
            {{ copy.sections.client }}
          </h4>
          <div class="grid gap-3">
            <Input
              v-model="contactSearch"
              :label="copy.fields.clientSearch"
              :placeholder="copy.placeholders.clientSearch"
              @input="searchContacts"
            />
            <div
              v-if="contactResults.length || isSearchingContacts"
              class="max-h-40 overflow-auto rounded-lg border border-n-weak bg-n-solid-1"
            >
              <Button
                v-for="contact in contactResults"
                :key="contact.id"
                type="button"
                color="slate"
                variant="ghost"
                justify="start"
                class="!h-auto w-full !flex-col !items-start !gap-1 !px-3 !py-2 text-left"
                @click="selectContact(contact)"
              >
                <span class="font-medium text-n-slate-12">
                  {{ contact.name || contact.phone_number || contact.email }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{
                    contact.phone_number || contact.email || contact.identifier
                  }}
                </span>
              </Button>
              <p
                v-if="!contactResults.length && !isSearchingContacts"
                class="p-3 text-sm text-n-slate-10"
              >
                {{ copy.empty.contacts }}
              </p>
            </div>

            <div class="grid gap-3 sm:grid-cols-2">
              <Input
                v-model="proposalForm.client_cpf"
                :label="copy.fields.clientCpf"
                :placeholder="copy.placeholders.cpf"
              />
              <div class="flex flex-col gap-1">
                <label class="mb-0.5 text-heading-3 text-n-slate-12">
                  {{ copy.fields.seller }}
                </label>
                <ComboBox
                  v-model="proposalForm.seller_id"
                  :options="agentSelectOptions"
                  class="w-full [&>select]:w-full"
                  teleport-to-body
                />
              </div>
            </div>
          </div>
        </section>

        <section class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ copy.sections.proposal }}
            </h4>
            <Button
              type="button"
              :label="copy.actions.newProduct"
              icon="i-lucide-plus"
              color="slate"
              variant="ghost"
              size="sm"
              @click="openProductDialog"
            />
          </div>
          <div class="grid gap-3">
            <Input
              v-model="proposalForm.title"
              required
              :label="copy.fields.title"
              :placeholder="copy.placeholders.proposalTitle"
            />

            <div class="grid items-end gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div class="flex flex-col gap-1">
                <span
                  class="mb-0.5 flex items-center justify-between gap-3 text-heading-3 text-n-slate-12"
                >
                  {{ copy.fields.product }}
                </span>
                <ComboBox
                  v-model="proposalForm.talkvia_credit_product_id"
                  :options="productSelectOptions"
                  class="w-full [&>select]:w-full"
                  teleport-to-body
                />
              </div>
              <Input
                v-model="proposalAmountDisplay"
                inputmode="numeric"
                :label="copy.fields.amount"
                :placeholder="copy.placeholders.amount"
              />
              <Input
                v-model="proposalForm.term"
                :label="copy.fields.term"
                :placeholder="copy.placeholders.term"
              />
            </div>

            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div class="flex flex-col gap-1">
                <label class="mb-0.5 text-heading-3 text-n-slate-12">
                  {{ copy.fields.phase }}
                </label>
                <ComboBox
                  v-model="proposalForm.stage"
                  :options="phaseOptions"
                  class="w-full [&>select]:w-full"
                  teleport-to-body
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="mb-0.5 text-heading-3 text-n-slate-12">
                  {{ copy.fields.status }}
                </label>
                <ComboBox
                  v-model="proposalForm.status"
                  :options="statusOptions"
                  class="w-full [&>select]:w-full"
                  teleport-to-body
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="mb-0.5 text-heading-3 text-n-slate-12">
                  {{ copy.fields.inbox }}
                </label>
                <ComboBox
                  v-model="proposalForm.inbox_id"
                  :options="inboxSelectOptions"
                  class="w-full [&>select]:w-full"
                  teleport-to-body
                />
              </div>
            </div>
          </div>
        </section>

        <section class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
          <h4 class="mb-4 text-sm font-semibold text-n-slate-12">
            {{ copy.sections.schedule }}
          </h4>
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <ProposalDateField
              v-model="proposalForm.client_payment_date"
              :label="copy.fields.clientPaymentDate"
            />
            <ProposalDateField
              v-model="proposalForm.expected_close_date"
              :label="copy.fields.expectedCloseDate"
            />
            <ProposalDateField
              v-model="proposalForm.next_follow_up_at"
              :label="copy.fields.nextFollowUpAt"
              with-time
            />
          </div>
          <div class="mt-4">
            <Input
              v-model="proposalForm.signature_url"
              :label="copy.fields.signatureUrl"
              type="url"
              :placeholder="copy.placeholders.signatureUrl"
            />
          </div>
        </section>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            :label="copy.actions.cancel"
            color="slate"
            variant="outline"
            @click="proposalDialogRef?.close()"
          />
          <Button
            type="button"
            :label="copy.actions.saveProposal"
            :is-loading="isSaving"
            :disabled="
              isSaving ||
              !proposalForm.title ||
              !proposalForm.status ||
              !proposalForm.stage
            "
            @click="saveProposal"
          />
        </div>
      </template>
    </Dialog>

    <Dialog
      ref="draftDialogRef"
      width="3xl"
      position="top"
      :title="
        selectedDraft
          ? draftContactLabel(selectedDraft)
          : copy.tabs.simulationDrafts
      "
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <div v-if="selectedDraft" class="grid gap-4">
        <div
          class="rounded-lg border border-n-weak bg-n-solid-2 p-4 text-sm text-n-slate-11"
        >
          <p class="mb-1 text-n-slate-12">
            {{ draftSummaryText(selectedDraft) }}
          </p>
          <p class="mb-0">{{ draftExpiryText(selectedDraft) }}</p>
        </div>
        <div class="grid gap-3">
          <article
            v-for="simulation in selectedDraft.simulations"
            :key="simulation.id"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-4"
          >
            <div class="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
                  {{ simulationTitle(simulation) }}
                </h4>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ copy.fields.simulationCode }}:
                  {{ simulation.codigo_simulacao || '-' }}
                </p>
              </div>
              <strong class="text-base text-n-slate-12">
                {{ formatMoney(simulation.released_amount) }}
              </strong>
            </div>
            <p class="mb-4 text-sm text-n-slate-11">
              {{ simulation.summary || '-' }}
            </p>
            <Button
              type="button"
              :label="copy.actions.acceptSimulation"
              :is-loading="isSaving"
              :disabled="isSaving"
              size="sm"
              @click="acceptSimulation(simulation)"
            />
          </article>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="productDialogRef"
      width="lg"
      :title="editingProductId ? copy.actions.edit : copy.actions.newProduct"
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <div class="grid gap-3">
        <Input
          v-model="productForm.name"
          required
          :label="copy.fields.productName"
          :placeholder="copy.placeholders.productName"
        />
        <ProductProviderField
          :key="editingProductId"
          v-model="productForm.provider"
          :label="copy.fields.provider"
        />
        <div class="grid gap-3 sm:grid-cols-2">
          <Input
            v-model="productForm.min_amount"
            :label="copy.fields.minAmount"
            type="number"
            min="0"
            step="0.01"
          />
          <Input
            v-model="productForm.max_amount"
            :label="copy.fields.maxAmount"
            type="number"
            min="0"
            step="0.01"
          />
        </div>
        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          <Checkbox v-model="productForm.active" />
          {{ copy.fields.active }}
        </label>
        <div class="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            :label="copy.actions.cancel"
            color="slate"
            variant="outline"
            @click="productDialogRef?.close()"
          />
          <Button
            type="button"
            :label="copy.actions.saveProduct"
            :is-loading="isSaving"
            :disabled="isSaving || !productForm.name"
            @click="saveProduct"
          />
        </div>
      </div>
    </Dialog>
  </section>
</template>
